import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PinVerificationModal = ({ 
  isOpen, 
  onClose, 
  onVerified, 
  propertyId,
  propertyName 
}) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (isOpen) {
      // Focus first input when modal opens
      setTimeout(() => {
        inputRefs[0].current?.focus();
      }, 100);
      setPin(['', '', '', '']);
      setError('');
    }
  }, [isOpen]);

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newPin = [...pin];
    newPin[index] = value.slice(-1); // Only take last digit
    setPin(newPin);
    setError('');
    
    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
    
    // Auto-submit when all digits entered
    if (value && index === 3 && newPin.every(d => d)) {
      verifyPin(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const verifyPin = async (pinCode) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API}/therapist-auth/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pin: pinCode,
          property_id: propertyId
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.detail || 'Invalid PIN');
        setPin(['', '', '', '']);
        inputRefs[0].current?.focus();
        return;
      }
      
      // Store therapist info in session
      sessionStorage.setItem('verified_therapist', JSON.stringify(data.therapist));
      sessionStorage.setItem('therapist_token', data.token);
      
      onVerified(data.therapist);
    } catch (err) {
      setError('Verification failed. Please try again.');
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const pinCode = pin.join('');
    if (pinCode.length === 4) {
      verifyPin(pinCode);
    } else {
      setError('Please enter your complete 4-digit PIN');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="pin-verification-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-xl">
            <Lock className="w-5 h-5 text-amber-600" />
            Enter Your PIN
          </DialogTitle>
          <DialogDescription>
            Enter your personal 4-digit PIN to access the Operations dashboard
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {propertyName && (
            <div className="text-center text-sm text-muted-foreground">
              <span className="font-medium">{propertyName}</span>
            </div>
          )}
          
          <div className="flex justify-center gap-3">
            {pin.map((digit, index) => (
              <Input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-14 h-14 text-center text-2xl font-bold border-2 focus:border-amber-500 focus:ring-amber-500"
                disabled={loading}
                data-testid={`pin-input-${index}`}
              />
            ))}
          </div>
          
          {error && (
            <p className="text-center text-sm text-red-500" data-testid="pin-error">
              {error}
            </p>
          )}
          
          <div className="text-center text-xs text-muted-foreground">
            Your PIN was sent to your registered email during onboarding.
            <br />
            Contact admin if you've forgotten your PIN.
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={loading || pin.some(d => !d)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="verify-pin-btn"
            >
              {loading ? 'Verifying...' : 'Verify PIN'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PinVerificationModal;
